import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  FileText, Clock, CheckCircle, XCircle, Plus,
  Send, ArrowRight, Eye, Loader2, Image as ImageIcon, Globe,
} from 'lucide-react';
import DesignerLayout from './DesignerLayout';
import DesignPreview from '../../components/shared/DesignPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:   { label: 'Pending Review', color: 'bg-amber-100 text-amber-700',    icon: Clock },
  approved:  { label: 'Approved',       color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected:  { label: 'Rejected',       color: 'bg-red-100 text-red-600',        icon: XCircle },
  published: { label: 'Published',      color: 'bg-blue-100 text-blue-700',      icon: Globe },
};

const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-all hover:-translate-y-0.5">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
    <p className="text-sm text-gray-500 font-medium mt-0.5">{label}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const DesignerDashboard = () => {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    try { setUser(JSON.parse(localStorage.getItem('user') || '{}')); } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token || !API_URL) { setLoading(false); return; }
    fetch(`${API_URL}/api/designer/templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setDesigns(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const normStatus = (d) => d.submission_status || d.status;
  const total     = designs.length;
  const pending   = designs.filter(d => normStatus(d) === 'pending').length;
  const approved  = designs.filter(d => normStatus(d) === 'approved').length;
  const rejected  = designs.filter(d => normStatus(d) === 'rejected').length;
  const published = designs.filter(d => normStatus(d) === 'published').length;
  const drafts    = designs.filter(d => !normStatus(d)).length;

  // Recent 5 submitted designs
  const recentSubmissions = designs
    .filter(d => d.status)
    .sort((a, b) => new Date(b.submitted_at || b.created_at) - new Date(a.submitted_at || a.created_at))
    .slice(0, 5);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <DesignerLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">Here's an overview of your template activity.</p>
          </div>
          <Button
            onClick={() => navigate('/designer/editor')}
            className="bg-violet-600 hover:bg-violet-700 gap-1.5 flex-shrink-0"
          >
            <Plus className="w-4 h-4" /> Create Template
          </Button>
        </div>

        {/* Stat Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-violet-600" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard label="Total Templates" value={total}     icon={FileText}    color="bg-violet-500"  sub={`${drafts} drafts`} />
              <StatCard label="Pending Review"  value={pending}   icon={Clock}       color="bg-amber-500"   sub="Awaiting admin" />
              <StatCard label="Approved"        value={approved}  icon={CheckCircle} color="bg-emerald-500" sub="Ready to publish" />
              <StatCard label="Published"       value={published} icon={Globe}       color="bg-blue-500"    sub="Live in library" />
              <StatCard label="Rejected"        value={rejected}  icon={XCircle}     color="bg-red-500"     sub="Needs revision" />
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/designer/editor')}
                className="bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-2xl p-5 text-left text-white hover:shadow-lg transition-all hover:-translate-y-0.5 group"
              >
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-3 group-hover:bg-white/30 transition-colors">
                  <Plus className="w-5 h-5" />
                </div>
                <p className="font-semibold text-base">Create New Template</p>
                <p className="text-violet-200 text-sm mt-0.5">Open the full design editor</p>
              </button>
              <button
                onClick={() => navigate('/designer/my-templates')}
                className="bg-white rounded-2xl border border-gray-100 p-5 text-left hover:shadow-md transition-all hover:-translate-y-0.5 group"
              >
                <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-violet-200 transition-colors">
                  <Send className="w-5 h-5 text-violet-600" />
                </div>
                <p className="font-semibold text-base text-gray-900">Submit for Review</p>
                <p className="text-gray-500 text-sm mt-0.5">Submit a saved template</p>
              </button>
            </div>

            {/* Recent Submissions */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <p className="font-semibold text-gray-900">Recent Submissions</p>
                <button
                  onClick={() => navigate('/designer/submissions')}
                  className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1"
                >
                  View all <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              {recentSubmissions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
                    <Send className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="font-medium text-gray-700">No submissions yet</p>
                  <p className="text-sm text-gray-400">Create a template and submit it for review.</p>
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1.5 mt-1" onClick={() => navigate('/designer/editor')}>
                    <Plus className="w-3.5 h-3.5" /> Create Template
                  </Button>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide w-16">Preview</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Template Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Submitted</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 tracking-wide">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {recentSubmissions.map(d => {
                      const cfg = STATUS_CONFIG[normStatus(d)] || STATUS_CONFIG.pending;
                      const StatusIcon = cfg.icon;
                      return (
                        <tr key={d.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-3">
                            <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden">
                              {/* Live preview (Fix C): render the design's pages via the
                                  canonical TemplateThumbnail pipeline instead of a stored
                                  raster that can go stale when later edits are autosaved. */}
                              <DesignPreview
                                design={d}
                                className="w-full h-full"
                                placeholder={(
                                  <div className="w-full h-full flex items-center justify-center">
                                    <ImageIcon className="w-5 h-5 text-gray-300" />
                                  </div>
                                )}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 truncate max-w-[200px]">{d.name}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs tabular-nums">
                            {formatDate(d.submitted_at)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${cfg.color}`}>
                              <StatusIcon className="w-3 h-3" /> {cfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => navigate('/designer/submissions')}
                              className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1 ml-auto"
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Rejected feedback alert */}
            {rejected > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">
                    {rejected} template{rejected > 1 ? 's' : ''} need{rejected === 1 ? 's' : ''} revision
                  </p>
                  <p className="text-xs text-red-600 mt-0.5">
                    Review admin feedback and resubmit to get approved.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/designer/submissions')}
                  className="border-red-300 text-red-700 hover:bg-red-100 flex-shrink-0"
                >
                  View Feedback
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </DesignerLayout>
  );
};

export default DesignerDashboard;
